/**
 * Global error boundary (ADR-0075, Phase 1).
 *
 * Catches render-time crashes so the app shows a recoverable message
 * instead of a white screen, and surfaces the most recent `trace_id` so a
 * user can quote it to support (it matches the server logs).
 *
 * Phase 1 is browser-local only — it logs to the console; it does NOT
 * transmit anything. The remote error sink is GlitchTip's browser SDK in
 * Phase 2 (which adds PII scrubbing per ADR-0009). React error boundaries
 * must be class components, hence this one.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { recentTraceIds } from './trace.js';

type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[regta] unhandled render error', {
      name: error.name,
      message: error.message,
      componentStack: info.componentStack,
      traceId: recentTraceIds()[0] ?? null,
    });
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const traceId = recentTraceIds()[0] ?? null;
    return (
      <div
        role="alert"
        style={{
          maxWidth: 480,
          margin: '15vh auto',
          padding: '2rem',
          fontFamily: 'system-ui, sans-serif',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: '1.25rem' }}>Något gick fel</h1>
        <p>Ett oväntat fel inträffade. Ladda om sidan och försök igen.</p>
        {traceId && (
          <p style={{ fontSize: '0.85rem', color: '#666' }}>
            Referens för support: <code>{traceId}</code>
          </p>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
        >
          Ladda om sidan
        </button>
      </div>
    );
  }
}

/**
 * Make unhandled promise rejections + uncaught errors visible in the
 * console with the correlation id. Browser-local (no transmission) until
 * Phase 2's GlitchTip SDK. Call once at startup.
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('unhandledrejection', (e) => {
    // eslint-disable-next-line no-console
    console.error('[regta] unhandled promise rejection', {
      reason: e.reason instanceof Error ? e.reason.message : String(e.reason),
      traceId: recentTraceIds()[0] ?? null,
    });
  });
  window.addEventListener('error', (e) => {
    // eslint-disable-next-line no-console
    console.error('[regta] uncaught error', {
      message: e.message,
      traceId: recentTraceIds()[0] ?? null,
    });
  });
}
